import { ReactNode, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface HorizontalScrollSectionProps {
  title?: ReactNode;
  titleIcon?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}

export const HorizontalScrollSection = ({
  title,
  titleIcon,
  children,
  action,
  className,
}: HorizontalScrollSectionProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <div className={cn("relative", className)}>
      {/* Header */}
      {(title || action) && (
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-2 md:gap-3">
            {titleIcon}
            {title && <h2 className="text-lg md:text-xl font-semibold">{title}</h2>}
          </div>
          <div className="flex items-center gap-2">
            {/* Desktop scroll buttons */}
            <div className="hidden md:flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full z-10"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scroll('left');
                }}
                type="button"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full z-10"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scroll('right');
                }}
                type="button"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {action}
          </div>
        </div>
      )}

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-1"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
};

// Card wrapper for consistent sizing in horizontal scroll
interface ScrollCardProps {
  children: ReactNode;
  className?: string;
}

export const ScrollCard = ({ children, className }: ScrollCardProps) => {
  return (
    <div 
      className={cn(
        // Mobile: show ~2.2 cards (45% width each)
        // Tablet: show ~3.2 cards
        // Desktop: show 4 cards
        "flex-shrink-0 w-[45%] sm:w-[31%] lg:w-[24%] snap-start",
        className
      )}
    >
      {children}
    </div>
  );
};
